import { useMemo } from "react";
import { SidebarItemsType } from "../types/sidebar";
import { useEffectivePermissions } from "./usePermission";
import useAuth from "./useAuth";

/**
 * Hook to filter navigation items based on user permissions
 * Returns only items the user has permission to access
 */
export const useFilteredNavItems = (
  navItems: Array<{ title: string; pages: SidebarItemsType[] }>
) => {
  const { currentUser } = useAuth();
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const { permissions, loading } = useEffectivePermissions(associationId);

  const filteredItems = useMemo(() => {
    if (loading || !permissions) {
      // While loading, show all items (or show minimal set)
      return navItems;
    }

    // Helper function to check if user has permission for an item
    const hasPermissionForItem = (item: SidebarItemsType): boolean => {
      // SuperAdmin has access to everything
      if (currentUser?.isSuperAdmin) {
        return true;
      }

      // If no permission required, item is accessible
      if (!item.permission) {
        return true;
      }

      const { resource, action } = item.permission;
      const allowedActions = permissions[resource] || [];

      return allowedActions.includes(action);
    };

    // Filter function that recursively filters items and their children
    const filterItems = (items: SidebarItemsType[]): SidebarItemsType[] => {
      return items
        .filter((item) => {
          // Skip disabled items
          if (item.disabled) {
            return false;
          }

          // Check permission for this item
          return hasPermissionForItem(item);
        })
        .map((item) => {
          // If item has children, filter them too
          if (item.children && item.children.length > 0) {
            const filteredChildren = filterItems(item.children);

            // If parent has permission but no children remain, hide parent
            if (filteredChildren.length === 0) {
              return null;
            }

            return {
              ...item,
              children: filteredChildren,
            };
          }

          return item;
        })
        .filter((item): item is SidebarItemsType => item !== null);
    };

    // Apply filtering to all nav sections
    return navItems
      .map((section) => ({
        ...section,
        pages: filterItems(section.pages),
      }))
      .filter((section) => section.pages.length > 0); // Remove empty sections
  }, [navItems, permissions, loading, currentUser]);

  return { filteredItems, loading };
};
